/**
 * AutoML Service — wraps /api/admin/ai/automl/
 * Phase 4.8-B — WQ-198
 */

// === Types ===

export interface SweepConfig {
  dataset_id: string;
  target_column: string;
  problem_type?: string;
  algorithms?: string[];
  max_trials?: number;
  time_budget_seconds?: number;
  metric?: string;
}

export interface SweepStatus {
  sweep_id: string;
  status: string;
  progress: number;
  trials_completed: number;
  trials_total: number;
  best_metric: number | null;
  best_algorithm: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface Experiment {
  id: string;
  name: string;
  description: string | null;
  status: string;
  dataset_id: string;
  target_column: string;
  problem_type: string;
  best_run_id: string | null;
  best_metric: number | null;
  run_count: number;
  created_at: string;
  updated_at: string;
}

export interface ExperimentRun {
  id: string;
  experiment_id: string;
  algorithm: string;
  hyperparameters: Record<string, unknown>;
  metrics: Record<string, number>;
  status: string;
  duration_seconds: number | null;
  created_at: string;
  is_best?: boolean;
}

export interface RunComparison {
  runs: ExperimentRun[];
  metrics_summary: Record<string, { min: number; max: number; mean: number }>;
}

export interface DatasetProfile {
  columns: number;
  rows: number;
  dtypes: Record<string, string>;
  missing: Record<string, number>;
  summary: Record<string, Record<string, unknown>>;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === API Functions ===

export async function startSweep(
  authFetch: FetchFn,
  config: SweepConfig
): Promise<SweepStatus> {
  const res = await authFetch('/api/admin/ai/automl/sweep', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!res.ok) throw new Error(`Failed to start sweep: ${res.status}`);
  return res.json();
}

export async function getSweepStatus(
  authFetch: FetchFn,
  sweepId: string
): Promise<SweepStatus> {
  const res = await authFetch(`/api/admin/ai/automl/sweep/${sweepId}/status`);
  if (!res.ok) throw new Error(`Failed to get sweep status: ${res.status}`);
  return res.json();
}

export async function cancelSweep(
  authFetch: FetchFn,
  sweepId: string
): Promise<{ message: string }> {
  const res = await authFetch(`/api/admin/ai/automl/sweep/${sweepId}/cancel`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to cancel sweep: ${res.status}`);
  return res.json();
}

export async function listExperiments(
  authFetch: FetchFn,
  filters?: {
    status?: string;
    problem_type?: string;
    catalog_entry_id?: string;
    limit?: number;
    offset?: number;
  }
): Promise<Experiment[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.problem_type) params.set('problem_type', filters.problem_type);
  if (filters?.catalog_entry_id) params.set('catalog_entry_id', filters.catalog_entry_id);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/automl/experiments${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list experiments: ${res.status}`);
  return res.json();
}

export async function getExperiment(authFetch: FetchFn, id: string): Promise<Experiment> {
  const res = await authFetch(`/api/admin/ai/automl/experiments/${id}`);
  if (!res.ok) throw new Error(`Failed to get experiment: ${res.status}`);
  return res.json();
}

export async function deleteExperiment(authFetch: FetchFn, id: string): Promise<void> {
  const res = await authFetch(`/api/admin/ai/automl/experiments/${id}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Failed to delete experiment: ${res.status}`);
}

export async function listRuns(authFetch: FetchFn, experimentId: string): Promise<ExperimentRun[]> {
  const res = await authFetch(`/api/admin/ai/automl/experiments/${experimentId}/runs`);
  if (!res.ok) throw new Error(`Failed to list runs: ${res.status}`);
  return res.json();
}

export async function getRun(
  authFetch: FetchFn,
  experimentId: string,
  runId: string
): Promise<ExperimentRun> {
  const res = await authFetch(`/api/admin/ai/automl/experiments/${experimentId}/runs/${runId}`);
  if (!res.ok) throw new Error(`Failed to get run: ${res.status}`);
  return res.json();
}

export async function compareRuns(
  authFetch: FetchFn,
  runIds: string[]
): Promise<RunComparison> {
  const res = await authFetch('/api/admin/ai/automl/experiments/compare', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ run_ids: runIds }),
  });
  if (!res.ok) throw new Error(`Failed to compare runs: ${res.status}`);
  return res.json();
}

export async function profileDataset(
  authFetch: FetchFn,
  body: { dataset_id: string }
): Promise<DatasetProfile> {
  const res = await authFetch('/api/admin/ai/automl/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Failed to profile dataset: ${res.status}`);
  return res.json();
}

// === WQ-237 (Phase 4.10 β2 — AutoMLWizard) helpers ===

export type ProblemType = 'classification' | 'regression' | 'clustering';

export interface ProblemTypeInference {
  problem_type: ProblemType;
  rationale: string | null;
}

/**
 * Best-effort problem-type inference. Calls the AutoML profile endpoint
 * (which already exists) and applies a simple heuristic against the
 * target column dtype + cardinality. Returns `null` if the dataset cannot
 * be profiled — the wizard treats `null` as "fall back to user picks
 * classification by default" without blocking on inference.
 *
 * Per WQ-237 guidance: "If the inference endpoint doesn't exist or returns
 * an error, default to classification, omit the rationale snippet, and let
 * the user pick. Wizard does not block on inference availability."
 */
export async function inferProblemType(
  authFetch: FetchFn,
  datasetId: string,
  targetColumn: string
): Promise<ProblemTypeInference | null> {
  try {
    const profile = await profileDataset(authFetch, { dataset_id: datasetId });
    const dtype = profile?.dtypes?.[targetColumn];
    const summary = profile?.summary?.[targetColumn] as Record<string, unknown> | undefined;
    if (!dtype) return null;

    const distinctRaw = summary?.distinct_count ?? summary?.unique;
    const distinct = typeof distinctRaw === 'number' ? distinctRaw : null;
    const rows = profile?.rows ?? null;

    const isFloat = /float|double|real|numeric/i.test(dtype);
    const isInt = /int/i.test(dtype);
    const isString = /str|object|text|varchar/i.test(dtype);
    const lowCardinality = distinct !== null && distinct <= 50;

    let problem_type: ProblemType = 'classification';
    let rationale: string | null = null;

    if (isFloat && (!lowCardinality || distinct === null)) {
      problem_type = 'regression';
      rationale = distinct !== null
        ? `Inferred regression: target '${targetColumn}' is ${dtype} with ${distinct} distinct values across ${rows ?? '?'} rows.`
        : `Inferred regression: target '${targetColumn}' has continuous dtype ${dtype}.`;
    } else if ((isInt || isString) && distinct !== null && distinct >= 2 && distinct <= 50) {
      problem_type = 'classification';
      rationale = `Inferred classification: target '${targetColumn}' has ${distinct} distinct values across ${rows ?? '?'} rows.`;
    } else if (isFloat && lowCardinality) {
      problem_type = 'classification';
      rationale = `Inferred classification: target '${targetColumn}' is ${dtype} but has only ${distinct} distinct values.`;
    } else {
      problem_type = 'classification';
      rationale = null;
    }

    return { problem_type, rationale };
  } catch {
    return null;
  }
}

/**
 * Source-aware re-export pointer for the wizard. The actual implementation
 * lives in `datasetService.ts` (it touches both /data-catalog and /datasets);
 * re-exporting here keeps wizard imports consistent.
 */
export { getDatasetSchema, listAvailableDatasets } from './datasetService';
export type { DatasetOption, AvailableDatasets, DatasetSchemaPayload } from './datasetService';
